import Button from 'components/common/buttons/Button';
import { Lightning } from '@lightningjs/sdk';
import { constants } from 'aliases';
import { translate } from 'support/translate';
import { HoverableComponent } from 'components/common/HoverableComponent';
import ActionsList from 'components/common/ActionsList';
import EmailInputField from '../common/EmailInputField';
import TextLabel from 'components/common/TextLabel';

export interface SignInSectionTemplateSpec
  extends Lightning.Component.TemplateSpec {
  emailValue: string;
  Title: typeof TextLabel;
  Content: {
    Email: {
      Description: typeof TextLabel;
      EmailInput: typeof EmailInputField;
    };
    Buttons: typeof ActionsList;
  };
}

const WIDTH = 684;
const HEIGHT = 522;
const X_OFFSET = 32;

const SPACING_NORMAL = 32;
const SPACING_SMALL = 16;

const TITLE_FONT_SIZE = 36;
const TITLE_FONT_STYLE = 'italic bold';

const CONTENT_HEIGHT = 448;

const EMAIL_HEIGHT = 202;
const EMAIL_OFFSET_Y = 50;
const EMAIL_DESC_FONT_SIZE = 22;
const EMAIL_DESC_LINE_HEIGHT = 35;
const EMAIL_INPUT_HEIGHT = 76;

const BUTTONS_HEIGHT = 146;
const BUTTON_HEIGHT = 65;
const BUTTON_FONT_SIZE = 18;

export default class SignInSection extends HoverableComponent<SignInSectionTemplateSpec> {
  private _Title = this.getByRef('Title')!;
  private _Content = this.getByRef('Content')!;
  private _Buttons = this._Content.getByRef('Buttons')!;
  private _Email = this._Content.getByRef('Email')!;
  private _Description = this._Email.getByRef('Description')!;
  private _EmailInput = this._Email.getByRef('EmailInput')!;

  get announce() {
    return [this._Title.text?.text, this._Description.text?.text];
  }

  set emailValue(value: string) {
    this._EmailInput.input = value;
  }

  static override _template(): Lightning.Component.Template<SignInSectionTemplateSpec> {
    return {
      x: X_OFFSET,
      w: WIDTH,
      h: HEIGHT,
      flex: {
        direction: 'column',
      },
      Title: {
        type: TextLabel,
        w: WIDTH,
        text: {
          text: translate('account.signin.remoteTitle'),
          textAlign: 'center',
          fontSize: TITLE_FONT_SIZE,
          fontStyle: TITLE_FONT_STYLE,
        },
      },
      Content: {
        y: EMAIL_OFFSET_Y,
        h: CONTENT_HEIGHT,
        w: WIDTH,
        flex: {
          direction: 'column',
        },
        Email: {
          h: EMAIL_HEIGHT,
          flex: { direction: 'column' },
          flexItem: {
            marginBottom: SPACING_NORMAL,
          },
          Description: {
            type: TextLabel,
            h: EMAIL_DESC_LINE_HEIGHT,
            w: WIDTH,
            text: {
              text: translate('account.signin.preferredSignInMethod'),
              textAlign: 'center',
              fontSize: EMAIL_DESC_FONT_SIZE,
              lineHeight: EMAIL_DESC_LINE_HEIGHT,
            },
            flexItem: {
              marginBottom: SPACING_SMALL,
            },
          },
          EmailInput: {
            type: EmailInputField,
            h: EMAIL_INPUT_HEIGHT,
            passSignals: {
              $onHover: '$onHover',
              $emailInputSelected: '$resetToEmailInput',
            },
            signals: {
              $onHover: '$onHover',
            },
            zIndex: constants.ui.zIndex.foregroundLayer + 2,
          },
        },
        Buttons: {
          h: BUTTONS_HEIGHT,
          type: ActionsList,
          flex: { direction: 'column' },
          spacing: SPACING_SMALL,
          signals: {
            $onHover: '$onHover',
            $signInLink: true,
            $usePassword: true,
          },
          items: [
            {
              type: Button,
              minWidth: WIDTH,
              h: BUTTON_HEIGHT,
              fontSize: BUTTON_FONT_SIZE,
              action: '$signInLink',
              label: translate('account.signin.signInLink'),
              radius: 3,
              zIndex: constants.ui.zIndex.foregroundLayer + 2,
              signals: {
                $onHover: '$onHover',
              },
              passSignals: {
                $signInLink: '$signInLink',
              },
            },
            {
              type: Button,
              minWidth: WIDTH,
              h: BUTTON_HEIGHT,
              fontSize: BUTTON_FONT_SIZE,
              action: '$usePassword',
              radius: 3,
              zIndex: constants.ui.zIndex.foregroundLayer + 2,
              label: translate('account.signin.usePassword'),
              signals: {
                $onHover: '$onHover',
              },
              passSignals: {
                $usePassword: '$usePassword',
              },
            },
          ],
        },
      },
    };
  }

  override _enable() {
    super._enable();
    this._setState('EmailInput');
  }

  private $onHover(target: Lightning.Component) {
    switch (target) {
      case this._EmailInput:
        this.signal('$onEmailInputHovered');
        this._setState('EmailInput');
        break;
      case this._Buttons:
        this.signal('$onButtonHovered');
        this._setState('Buttons');
        break;
    }
  }

  private $signInLink() {
    this.signal('$signInLink', this._EmailInput.input);
  }

  private $usePassword() {
    this.signal('$usePassword', this._EmailInput.input);
  }

  static override _states() {
    return [
      class EmailInput extends this {
        override _getFocused() {
          return this._EmailInput;
        }

        override _handleDown() {
          this._setState('Buttons');
        }
      },
      class Buttons extends this {
        override _getFocused() {
          return this._Buttons;
        }

        override _handleUp() {
          this._setState('EmailInput');
        }
      },
    ];
  }
}
